import { useState } from "react";
import { toast } from "sonner";
import { X, Upload, Trash2, Save } from "lucide-react";
import { api, API, getAdminToken } from "@/lib/api";
import { adminAuth, chunkedUpload, BACKEND, STATUS_LABELS } from "@/lib/ceda";

const GROUPS = [
  ["1. Fiche descriptive", [["code", "Code CEDA (ex : CEDA 05)"], ["title", "Titre"], ["intention", "Intention", 2], ["presentation", "Présentation", 4], ["benefit", "Bénéfice de bien-être recherché", 2], ["usage_tips", "Conseils d'utilisation", 3]]],
  ["2. Posologie de pratique", [["frequency", "Fréquence suggérée"], ["session_duration", "Durée d'une séance"], ["best_moment", "Moment conseillé"], ["precautions", "Précautions éventuelles", 2]]],
  ["4. Exercice d'intégration", [["integration_exercise", "Action simple, question de réflexion ou petit rituel", 3]]],
];

export const AdminCedaCapsuleForm = ({ initial, families, onClose, onSaved }) => {
  const [c, setC] = useState({ status: "draft", family_id: families[0]?.id || "", ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setC((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...c, order: Number(c.order) || 0 };
      const r = c.id ? await api.put(`/admin/ceda/capsules/${c.id}`, body, adminAuth()) : await api.post("/admin/ceda/capsules", body, adminAuth());
      setC(r.data); onSaved();
      toast.success(c.id ? "Capsule enregistrée" : "Capsule créée — vous pouvez ajouter les fichiers");
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="admin-ceda-capsule-form" onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-6 md:p-8 max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[#1F2A24]">{c.id ? `Capsule ${c.code || ""}` : "Nouvelle capsule"}</h2>
          <button onClick={onClose} data-testid="admin-ceda-form-close" className="p-1.5 rounded-full hover:bg-[#F6F2E9]"><X size={18} /></button>
        </div>
        <div className="mt-5 grid sm:grid-cols-3 gap-3">
          <Field label="Famille"><select data-testid="admin-ceda-field-family" value={c.family_id} onChange={(e) => set("family_id", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white">{families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}</select></Field>
          <Field label="Statut"><select data-testid="admin-ceda-field-status" value={c.status} onChange={(e) => set("status", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white">{Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="Ordre d'affichage"><input value={c.order ?? ""} onChange={(e) => set("order", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" /></Field>
        </div>
        <div className="flex flex-col">
        {GROUPS.map(([title, fields], gi) => (
          <fieldset key={title} className="mt-6" style={{ order: gi === 2 ? 4 : gi + 1 }}>
            <legend className="font-serif text-lg text-[#385E3A]">{title}</legend>
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              {fields.map(([k, label, rows]) => (
                <Field key={k} label={label} wide={!!rows}>
                  {rows ? <textarea data-testid={`admin-ceda-field-${k}`} rows={rows} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                    : <input data-testid={`admin-ceda-field-${k}`} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />}
                </Field>
              ))}
            </div>
          </fieldset>
        ))}
        <fieldset className="mt-6" style={{ order: 3 }}>
          <legend className="font-serif text-lg text-[#385E3A]">3. Média guidé et fichiers</legend>
          {c.id ? (
            <div className="mt-2 space-y-3">
              <FileSlot capsule={c} kind="media" label="Audio ou vidéo guidée (un seul média principal)" accept="audio/*,video/*,.mp3,.m4a,.wav,.mp4,.webm,.mov" current={c.has_media && `${c.media_type} · ${c.media_filename}`} onDone={(cap) => { setC(cap); onSaved(); }} />
              {c.has_media && (c.media_type === "audio"
                ? <audio key={c.updated_at} controls src={`${API}/admin/ceda/capsules/${c.id}/media?token=${getAdminToken()}`} className="w-full" data-testid="admin-ceda-media-preview" />
                : <video key={c.updated_at} controls src={`${API}/admin/ceda/capsules/${c.id}/media?token=${getAdminToken()}`} className="w-full rounded-xl bg-black aspect-video" data-testid="admin-ceda-media-preview" />)}
              <FileSlot capsule={c} kind="pdf" label="Fiche PDF (facultatif)" accept="application/pdf" current={c.has_pdf && c.pdf_filename} onDone={(cap) => { setC(cap); onSaved(); }} />
              <FileSlot capsule={c} kind="cover" label="Illustration (facultatif)" accept="image/*" current={c.cover_url && "illustration chargée"} preview={c.cover_url && `${BACKEND}${c.cover_url}?admin=${getAdminToken()}&t=${c.updated_at || ""}`} onDone={(cap) => { setC(cap); onSaved(); }} />
            </div>
          ) : <p className="font-sans text-sm text-[#1F2A24]/60 mt-2">Enregistrez d'abord la capsule pour pouvoir ajouter les fichiers.</p>}
        </fieldset>
        </div>
        <div className="flex justify-end gap-3 mt-7">
          <button onClick={onClose} className="rounded-full px-5 py-2.5 font-ui text-sm text-[#1F2A24]/70">Fermer</button>
          <button data-testid="admin-ceda-save" disabled={saving} onClick={save} className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-6 py-2.5 font-ui text-sm hover:bg-[#2c4a2e] disabled:opacity-60"><Save size={15} /> Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, wide, children }) => (
  <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
    <span className="font-sans text-xs uppercase tracking-wider text-[#1F2A24]/55">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const FileSlot = ({ capsule, kind, label, accept, current, preview, onDone }) => {
  const [pct, setPct] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    setPct(0);
    try {
      const res = await chunkedUpload(capsule.id, kind, file, setPct);
      toast.success("Fichier chargé"); onDone(res.capsule);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    setPct(null);
  };
  const remove = async () => {
    if (!window.confirm("Retirer ce fichier ?")) return;
    await api.delete(`/admin/ceda/capsules/${capsule.id}/file/${kind}`, adminAuth());
    const r = await api.get("/admin/ceda/capsules", adminAuth());
    onDone(r.data.find((x) => x.id === capsule.id));
    toast.success(kind === "media" ? "Média retiré — capsule repassée en brouillon" : "Fichier retiré");
  };
  return (
    <div className="rounded-xl border border-[#E8DCC4] p-3 flex flex-wrap items-center gap-3" data-testid={`admin-ceda-slot-${kind}`}>
      {preview && <img src={preview} alt="" className="w-12 h-12 rounded-lg object-cover" />}
      <div className="flex-1 min-w-[180px]">
        <p className="font-sans text-sm text-[#1F2A24]">{label}</p>
        <p className="font-sans text-xs text-[#1F2A24]/55">{current || "aucun fichier"}</p>
        {pct !== null && <div className="h-1.5 rounded-full bg-[#E8DCC4] mt-2 overflow-hidden"><div className="h-full bg-[#C9A24B] transition-[width]" style={{ width: `${pct}%` }} data-testid={`admin-ceda-progress-${kind}`} /></div>}
      </div>
      <input type="file" accept={accept} id={`ceda-${kind}-${capsule.id}`} className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid={`admin-ceda-file-${kind}`} />
      <label htmlFor={`ceda-${kind}-${capsule.id}`} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs cursor-pointer"><Upload size={12} /> {pct !== null ? `${pct} %` : current ? "Remplacer" : "Ajouter"}</label>
      {current && <button onClick={remove} data-testid={`admin-ceda-remove-${kind}`} className="p-1.5 rounded border text-[#A63B36]"><Trash2 size={13} /></button>}
    </div>
  );
};
